import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ShieldAlert, ShieldPlus, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field, Select } from '@/components/ui/field';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiMessage, fullName } from '../helpers';
import {
  EMPTY_SCOPE,
  isScopeComplete,
  scopeIdOf,
  type ResolvedAssignment,
  type Role,
  type RoleDetail,
  type ScopeSelection,
  type Staff,
  type StaffDetail,
} from '../types';
import ScopePicker from './ScopePicker';
import ConfirmDialog from './ConfirmDialog';

interface Props {
  initialStaffId: string | null;
  onStaffChange: (staffId: string | null) => void;
}

/**
 * P1-09 — scoped role assignments (§24.4).
 *
 * A grant is a triple: who, which role, and where. `GET /users/:id` already
 * resolves each existing grant to a scope name through the ownership chain, so
 * the table can show "Manager · Floor · Ground Floor" rather than a raw cuid.
 */
export default function ScopeAssignmentsPanel({ initialStaffId, onStaffChange }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const [staffId, setStaffId] = useState(initialStaffId ?? '');
  useEffect(() => {
    if (initialStaffId) setStaffId(initialStaffId);
  }, [initialStaffId]);

  const staffQuery = useApi<unknown>('/users', { limit: 200 });
  const rolesQuery = useApi<unknown>('/roles', { limit: 200 });
  const detailQuery = useApi<StaffDetail>(staffId ? `/users/${staffId}` : null);

  const staff = rowsOf<Staff>(staffQuery.data).rows;
  const roles = rowsOf<Role>(rolesQuery.data).rows;
  const detail = detailQuery.data;

  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<ScopeSelection>(EMPTY_SCOPE);
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState<ResolvedAssignment | null>(null);

  const canGrant = can('users.update');

  const assignments = useMemo(() => detail?.roleAssignments ?? [], [detail]);

  /**
   * `UsersService.grantRole` runs the escalation guard over the ROLE's whole
   * permission set, so which role is picked decides whether the grant is
   * possible at all — `users.update` on its own is not enough. The list rows
   * only carry a count, so the chosen role's keys are read here.
   */
  const roleDetailQuery = useApi<RoleDetail>(roleId ? `/roles/${roleId}` : null);
  // The fetch trails the selection by a render, and the stale answer belongs to
  // a different role — judging a grant on it would flip the button either way.
  const chosenRole = roleDetailQuery.data?.id === roleId ? roleDetailQuery.data : null;

  const escalating = useMemo(() => {
    if (!chosenRole) return [];
    return (chosenRole.permissionKeys ?? []).filter((key) => !can(key));
  }, [chosenRole, can]);

  /**
   * `RoleAssignment` is unique on (user, role, level, scope), and the service
   * answers 409 for a repeat. The grants are already on screen, so the clash is
   * knowable before the request rather than after it.
   */
  const duplicateGrant = useMemo(() => {
    const scopeId = scopeIdOf(scope) ?? null;
    return assignments.some(
      (assignment) =>
        assignment.role.id === roleId &&
        assignment.scopeLevel === scope.level &&
        (assignment.scopeId ?? null) === scopeId,
    );
  }, [assignments, roleId, scope]);

  /** Every reason the server would refuse this grant, in the order it checks them. */
  const grantBlock: string | null = !staffId
    ? t('settings.assignments.selectStaff')
    : !roleId
      ? t('settings.assignments.selectRole')
      : !isScopeComplete(scope)
        ? t('settings.assignments.scopeIncomplete')
        : roleDetailQuery.error
          ? roleDetailQuery.error
          : !chosenRole
            ? t('settings.assignments.roleLoading')
            : escalating.length > 0
              ? t('settings.assignments.blockedEscalation', { n: escalating.length })
              : duplicateGrant
                ? t('settings.assignments.alreadyHeld')
                : null;

  const ready = grantBlock === null;

  const selectStaff = (id: string) => {
    setStaffId(id);
    onStaffChange(id || null);
    setScope(EMPTY_SCOPE);
    setRoleId('');
  };

  const grant = async () => {
    if (!ready) return;
    setGranting(true);
    try {
      const scopeId = scopeIdOf(scope);
      await api.post(`/users/${staffId}/roles`, {
        roleId,
        scopeLevel: scope.level,
        // TENANT scope takes no scopeId — sending one is a 400.
        ...(scopeId ? { scopeId } : {}),
      });
      toast.success(t('settings.assignments.granted'));
      setRoleId('');
      setScope(EMPTY_SCOPE);
      detailQuery.reload();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    } finally {
      setGranting(false);
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    try {
      await api.delete(`/users/${staffId}/roles/${revoking.id}`);
      toast.success(t('settings.assignments.revoked'));
      detailQuery.reload();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.assignments.title')}</CardTitle>
          <CardDescription>{t('settings.assignments.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Field label={t('settings.assignments.staffMember')} required>
            <Select
              className="max-w-md"
              value={staffId}
              disabled={staffQuery.loading}
              onChange={(e) => selectStaff(e.target.value)}
            >
              <option value="">
                {staffQuery.loading ? t('common.loading') : t('settings.assignments.selectStaff')}
              </option>
              {staff.map((person) => (
                <option key={person.id} value={person.id}>
                  {fullName(person)}
                  {person.isActive ? '' : ` — ${t('settings.common.inactive')}`}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {!staffId ? (
        <EmptyState
          title={t('settings.assignments.pickStaffTitle')}
          hint={t('settings.assignments.pickStaffHint')}
        />
      ) : detailQuery.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : detailQuery.error ? (
        <ErrorState message={detailQuery.error} onRetry={detailQuery.reload} />
      ) : (
        <>
          {canGrant && (
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.assignments.grantTitle')}</CardTitle>
                <CardDescription>{t('settings.assignments.grantHint')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('settings.assignments.role')} required>
                    <Select
                      value={roleId}
                      disabled={rolesQuery.loading}
                      onChange={(e) => setRoleId(e.target.value)}
                    >
                      <option value="">
                        {rolesQuery.loading
                          ? t('common.loading')
                          : t('settings.assignments.selectRole')}
                      </option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <ScopePicker value={scope} onChange={setScope} disabled={granting} />

                {escalating.length > 0 && (
                  <p className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-900 dark:text-amber-200">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {t('settings.assignments.blockedEscalationHelp', {
                        role: chosenRole?.name ?? '',
                        keys: escalating.slice(0, 6).join(', '),
                        n: escalating.length,
                      })}
                    </span>
                  </p>
                )}

                <div className="flex flex-wrap items-center justify-end gap-3">
                  {grantBlock && (
                    <span className="text-xs text-ink-muted">{grantBlock}</span>
                  )}
                  <Button
                    onClick={() => void grant()}
                    disabled={!ready || granting}
                    title={grantBlock ?? undefined}
                  >
                    <ShieldPlus className="h-4 w-4" />
                    {granting ? t('settings.common.saving') : t('settings.assignments.grant')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('settings.assignments.role')}</TH>
                  <TH>{t('settings.scope.levelLabel')}</TH>
                  <TH>{t('settings.assignments.scopeName')}</TH>
                  <TH>{t('settings.assignments.grantedAt')}</TH>
                  <TH className="text-end">{t('settings.common.actions')}</TH>
                </TR>
              </THead>
              <tbody>
                {assignments.length === 0 ? (
                  <TableEmpty colSpan={5}>{t('settings.assignments.empty')}</TableEmpty>
                ) : (
                  assignments.map((assignment) => (
                    <TR key={assignment.id}>
                      <TD>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{assignment.role.name}</span>
                          {assignment.role.isSystem && (
                            <Badge tone="info">{t('settings.roles.system')}</Badge>
                          )}
                        </div>
                        <span className="text-xs text-ink-muted">
                          {t('settings.assignments.permissionsCount', {
                            count: assignment.role.permissionKeys?.length ?? 0,
                          })}
                        </span>
                      </TD>
                      <TD>
                        <Badge tone="brand">
                          {t(`settings.scope.level.${assignment.scopeLevel}`)}
                        </Badge>
                      </TD>
                      <TD>
                        {assignment.scopeName ??
                          (assignment.scopeId
                            ? t('settings.assignments.scopeMissing')
                            : t('settings.scope.tenantWide'))}
                      </TD>
                      <TD className="text-ink-muted">
                        {formatDateTime(assignment.createdAt, i18n.language)}
                      </TD>
                      <TD>
                        <div className="flex justify-end">
                          {canGrant && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={t('settings.assignments.revoke')}
                              onClick={() => setRevoking(assignment)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}

      <ConfirmDialog
        open={revoking !== null}
        title={t('settings.assignments.revoke')}
        message={t('settings.assignments.revokeConfirm', {
          role: revoking?.role.name ?? '',
          scope:
            revoking?.scopeName ??
            (revoking ? t(`settings.scope.level.${revoking.scopeLevel}`) : ''),
        })}
        confirmLabel={t('settings.assignments.revoke')}
        onConfirm={revoke}
        onClose={() => setRevoking(null)}
      />
    </div>
  );
}
